import type { GetApiInstitutionParams, InstitutionViewDTO } from '@smartsash/api-client';
import type { z } from 'zod';

import { createAdminListSearchSchema } from '@/router/listSearch';
import {
  buildFilterParams,
  collectSearchTerms,
  sortParams,
  type FilterParamMap,
} from '@/utils/listQueryParams';

export const institutionsSearchSchema = createAdminListSearchSchema();

export type InstitutesSearch = z.infer<typeof institutionsSearchSchema>;

/** The one column whose values the endpoint can filter on, and the field id the builder uses. */
export const INSTITUTES_CITY_FIELD = 'city';

/**
 * Column id → the endpoint's `sortBy` value. `GET /api/Institution` validates this against its own
 * whitelist, so a column absent here is a column the server cannot order by and must not be marked
 * `sortable`. Phone is the one such column: the endpoint has no `phoneNumber` sort.
 */
export const INSTITUTES_SORT_KEYS: Readonly<Record<string, string>> = {
  company: 'name',
  city: 'city',
  contact: 'contact',
  email: 'email',
};

export const INSTITUTES_FILTER_PARAMS: FilterParamMap<GetApiInstitutionParams> = {
  [INSTITUTES_CITY_FIELD]: { kind: 'multi', param: 'city' },
};

/**
 * Turn the URL's `page`/`q`/`sort`/`filters` into `GetApiInstitutionParams` (HS-869 follow-up).
 * Shared by the screen's own `query` and `InstitutionsFilter`'s cache read, which have to build the
 * exact same params or the filter panel would read a different cache entry than the table renders
 * from.
 */
export function institutionsQueryParams(search: InstitutesSearch): GetApiInstitutionParams {
  const conditions = search.filters ?? [];
  const searchTerms = collectSearchTerms(conditions, search.q ?? '');
  return {
    page: search.page,
    pageSize: search.pageSize,
    ...sortParams<GetApiInstitutionParams>(search.sort, INSTITUTES_SORT_KEYS),
    ...(searchTerms ? { search: searchTerms } : {}),
    ...buildFilterParams(conditions, INSTITUTES_FILTER_PARAMS),
  };
}

/**
 * The text each column holds for a row — the one source the table, the client-scope search and
 * sort, and the filter builder's vocabulary all read (HS-869).
 *
 * Lives apart from the screen definition so the filter component can share it without importing
 * the definition that renders the filter.
 */
export const institutionsColumnText = {
  company: (row: InstitutionViewDTO) => `${row.name ?? ''} ${row.accountNumber ?? ''}`.trim(),
  city: (row: InstitutionViewDTO) => row.city ?? '',
  contact: (row: InstitutionViewDTO) => row.contact ?? '',
  email: (row: InstitutionViewDTO) => row.email ?? '',
  phone: (row: InstitutionViewDTO) => row.phoneNumber ?? '',
} as const;

export type InstitutesColumnId = keyof typeof institutionsColumnText;
